import { forwardRef, ReactNode, HTMLProps } from 'react';
import clsx from 'clsx';

import {
  Spinner,
  Typography,
  type WithErrorMessageComputedProps,
  type WithErrorMessageProps,
  withErrorMessage
} from '@ag1/ui';
import styles from './Input.module.scss';

type ArticleEmailSubscriptionInputBaseProps = {
  sectionType?: string;
  buttonLabel?: string;
  successMessage?: ReactNode;
  isLoading?: boolean;
  isSuccess?: boolean;
  className?: string;
} & WithErrorMessageComputedProps &
  HTMLProps<HTMLInputElement>;

type ArticleEmailSubscriptionInputProps = Omit<
  ArticleEmailSubscriptionInputBaseProps,
  keyof WithErrorMessageComputedProps
> &
  WithErrorMessageProps;

const ArticleEmailSubscriptionInputBase = forwardRef<
  HTMLInputElement,
  ArticleEmailSubscriptionInputBaseProps
>(
  (
    {
      buttonLabel,
      isSuccess,
      successMessage,
      hasError,
      errorMessage,
      isLoading,
      className,
      ...inputProps
    },
    ref
  ) => (
    <div
      className={clsx(
        styles.container,
        hasError && styles.error,
        isSuccess && styles.success,
        isLoading && styles.loading,
        className
      )}>
      {!isSuccess && (
        <>
          <input
            {...inputProps}
            ref={ref}
            type="text"
            className={styles.input}
            data-testid="article-email-subscription-input"
            aria-label="article email subscription input"
          />
          <button
            type="submit"
            aria-label={buttonLabel}
            className={styles.button}
            data-testid="article-email-subscription-submit-button">
            {isLoading ? (
              <Spinner size={24} color="brand-white" withOverlay={false} />
            ) : (
              buttonLabel
            )}
          </button>
          {hasError && (
            <Typography variant="portalCaption" size="1" noPadding className={styles.messageUnder}>
              {errorMessage}
            </Typography>
          )}
        </>
      )}
      {isSuccess && (
        <Typography variant="portalText" size="2" noPadding className={styles.message}>
          {successMessage}
        </Typography>
      )}
    </div>
  )
);

ArticleEmailSubscriptionInputBase.displayName = 'ArticleEmailSubscriptionInputBase';

export default withErrorMessage<HTMLInputElement, ArticleEmailSubscriptionInputProps>(
  ArticleEmailSubscriptionInputBase
);
